import type { PageBlocksMeasurement, SplitableHeights } from './pageDelimitationMeasure';

import { getPageHeightInPx } from '../utils/page-utils';

export interface EditableSectionSplit {
  showTitle: boolean;
  startIndex: number;
  endIndex: number;
}

export interface ComputeListSplitsOptions {
  gapPx: number;
  bodyBottomPaddingPx: number;
  splitableSections: [number, number][];
  pageBottomLogoPx?: number;
  /** Top padding applied to pages after the first one. Defaults to gapPx if not set. */
  pageTopPaddingPx?: number;
}

export interface ListSplitsResult {
  blockOffsets: number[][];
  splitLayout: (EditableSectionSplit[] | null)[][];
  pageCount: number;
  pageHeightPx: number;
  safePageHeightPx: number;
  allBlockHeights: number[][];
  bodyStartPxPerColumn: number[];
}

export interface LayoutBlockMeta {
  col: number;
  blockIndex: number;
  sectionIndex: number;
  chunkIndex: number;
  splitableKey: string;
  isSplitable: boolean;
  chunk: EditableSectionSplit;
}

const PRINT_SAFETY_MARGIN_PX = 12;
const CONTINUATION_HEADER_PX = 12;
const PAGE_COUNT_TOLERANCE_PX = 10;

function computePageCount(maxBottomPx: number, pageHeightPx: number): number {
  const fullPages = Math.floor(maxBottomPx / pageHeightPx);
  const remainderPx = maxBottomPx % pageHeightPx;
  return fullPages + (remainderPx > PAGE_COUNT_TOLERANCE_PX ? 1 : 0) || 1;
}

function getSectionItemCount(
  splitableByKey: Map<string, SplitableHeights>,
  col: number,
  sectionIndex: number,
): number {
  return splitableByKey.get(`${col}-${sectionIndex}`)?.itemHeights.length ?? 0;
}

function getSectionChunks(
  splitableByKey: Map<string, SplitableHeights>,
  col: number,
  sectionIndex: number,
  sectionSplits: EditableSectionSplit[] | null,
): EditableSectionSplit[] {
  if (sectionSplits?.length) {
    return sectionSplits;
  }

  return [
    {
      showTitle: true,
      startIndex: 0,
      endIndex: getSectionItemCount(splitableByKey, col, sectionIndex),
    },
  ];
}

function getSectionHeightForPageCount(
  sectionHeights: number[],
  bodyStartPx: number,
  gapPx: number,
): number {
  return sectionHeights.reduce((bottomPx, height, index) => {
    const gapAfter = index < sectionHeights.length - 1 ? gapPx : 0;
    return bottomPx + height + gapAfter;
  }, bodyStartPx);
}

export function createInitialListSplitsResult(
  measurement: PageBlocksMeasurement,
  options: ComputeListSplitsOptions,
): ListSplitsResult {
  const { gapPx, bodyBottomPaddingPx, pageBottomLogoPx = 0, pageTopPaddingPx = gapPx } = options;
  const pageHeightPx = getPageHeightInPx();
  const safePageHeightPx = Math.max(0, pageHeightPx - PRINT_SAFETY_MARGIN_PX - pageBottomLogoPx);
  const splitLayout = measurement.sectionHeights.map(column => column.map(() => null));
  const blockOffsets = measurement.sectionHeights.map(column => column.map(() => 0));
  const maxBottomPx = measurement.sectionHeights.reduce((maxBottom, columnHeights, col) => {
    const bodyStartPx = measurement.bodyStartPxPerColumn[col] ?? 0;
    const bottomPx =
      getSectionHeightForPageCount(columnHeights, bodyStartPx, gapPx) + bodyBottomPaddingPx;
    return Math.max(maxBottom, bottomPx);
  }, 0);

  // Estimate extra top padding for pages after the first (each page transition adds pageTopPaddingPx - gapPx)
  const initialPageCount = computePageCount(maxBottomPx, pageHeightPx);
  const extraPadding = Math.max(0, initialPageCount - 1) * Math.max(0, pageTopPaddingPx - gapPx);

  return {
    blockOffsets,
    splitLayout,
    pageCount: computePageCount(maxBottomPx + extraPadding, pageHeightPx),
    pageHeightPx,
    safePageHeightPx,
    allBlockHeights: measurement.sectionHeights.map(column => [...column]),
    bodyStartPxPerColumn: [...measurement.bodyStartPxPerColumn],
  };
}

export function buildBlockMetas(
  measurement: PageBlocksMeasurement,
  splitLayout: (EditableSectionSplit[] | null)[][],
): LayoutBlockMeta[][] {
  return measurement.sectionHeights.map((sections, col) => {
    let blockIndex = 0;
    const metas: LayoutBlockMeta[] = [];

    sections.forEach((_, sectionIndex) => {
      const splitableKey = `${col}-${sectionIndex}`;
      const isSplitable = measurement.splitableByKey.has(splitableKey);
      const chunks = getSectionChunks(
        measurement.splitableByKey,
        col,
        sectionIndex,
        splitLayout[col]?.[sectionIndex] ?? null,
      );

      chunks.forEach((chunk, chunkIndex) => {
        metas.push({
          col,
          blockIndex,
          sectionIndex,
          chunkIndex,
          splitableKey,
          isSplitable,
          chunk,
        });
        blockIndex += 1;
      });
    });

    return metas;
  });
}

export function createZeroOffsetsForSplitLayout(
  measurement: PageBlocksMeasurement,
  splitLayout: (EditableSectionSplit[] | null)[][],
): number[][] {
  return buildBlockMetas(measurement, splitLayout).map(column => column.map(() => 0));
}

export function computePageCountFromBottom(maxBottomPx: number, pageHeightPx: number): number {
  return computePageCount(maxBottomPx, pageHeightPx);
}

export function splitChunkByAvailableSpace(
  splitable: SplitableHeights,
  chunk: EditableSectionSplit,
  availableHeightPx: number,
): { head: EditableSectionSplit; tail: EditableSectionSplit } | null {
  const chunkLength = chunk.endIndex - chunk.startIndex;
  if (chunkLength <= 1) {
    return null;
  }

  const headerHeight = chunk.showTitle ? splitable.headerHeight : CONTINUATION_HEADER_PX;
  let usedHeight = headerHeight;
  let fittingItems = 0;

  for (let index = chunk.startIndex; index < chunk.endIndex; index++) {
    const itemHeight = splitable.itemHeights[index] ?? 0;
    const gapBefore = fittingItems > 0 ? splitable.itemGapPx : 0;
    const nextUsedHeight = usedHeight + gapBefore + itemHeight;
    const isLastItemOfChunk = index === chunk.endIndex - 1;
    const heightWithFooter = nextUsedHeight + (isLastItemOfChunk ? splitable.footerHeight : 0);

    if (heightWithFooter <= availableHeightPx) {
      usedHeight = nextUsedHeight;
      fittingItems += 1;
      continue;
    }

    break;
  }

  if (fittingItems <= 0 || fittingItems >= chunkLength) {
    return null;
  }

  return {
    head: {
      showTitle: chunk.showTitle,
      startIndex: chunk.startIndex,
      endIndex: chunk.startIndex + fittingItems,
    },
    tail: {
      showTitle: false,
      startIndex: chunk.startIndex + fittingItems,
      endIndex: chunk.endIndex,
    },
  };
}

export function applyChunkSplit(
  measurement: PageBlocksMeasurement,
  splitLayout: (EditableSectionSplit[] | null)[][],
  meta: LayoutBlockMeta,
  split: { head: EditableSectionSplit; tail: EditableSectionSplit },
): (EditableSectionSplit[] | null)[][] {
  return splitLayout.map((column, col) =>
    column.map((sectionSplits, sectionIndex) => {
      if (col !== meta.col || sectionIndex !== meta.sectionIndex) {
        return sectionSplits;
      }

      const currentChunks = getSectionChunks(
        measurement.splitableByKey,
        col,
        sectionIndex,
        sectionSplits,
      );
      const nextChunks = currentChunks.flatMap((currentChunk, chunkIndex) => {
        if (chunkIndex !== meta.chunkIndex) {
          return [currentChunk];
        }

        return [split.head, split.tail];
      });

      const itemCount = getSectionItemCount(measurement.splitableByKey, col, sectionIndex);
      if (
        nextChunks.length === 1 &&
        nextChunks[0]?.showTitle &&
        nextChunks[0].startIndex === 0 &&
        nextChunks[0].endIndex === itemCount
      ) {
        return null;
      }

      return nextChunks;
    }),
  );
}
